/* ===========================================================
   panels/Merge.jsx — "Merge changes back" (copy → original)

   Three snapshots: where the copy started (stored inside the copy),
   the original now, the copy now. Changes made on only one side go in
   by themselves. Spots changed on both sides are shown side by side
   and you pick: keep the original's, keep the copy's, or keep both
   (the default, so nothing is ever lost by accident).

   Works offline too: the result is an ordinary edit to the original,
   saved on this device and synced when the internet is back.
   =========================================================== */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { Dialog, Button, Spinner, openModal, toast } from '../ui/ui.jsx';
import { ProtoCard } from '../ui/proto.jsx';
import { merge3 } from '../../shared/merge3.js';
import { toJSON, applyJSON, normalize } from '../editor/convert.js';
import { DocSync } from '../sync/provider.js';
import { connector } from '../sync/connect.js';
import { saveVersion } from '../sync/versions.js';
import { store, online } from '../state.js';
import { findDoc } from '../sync/docs.js';
import { api } from '../lib/api.js';
import { Html } from './Compare.jsx';
import { w } from '../lib/words.js';
import { go } from '../lib/router.js';

/** Open a doc's live copy without an editor (for merging). Resolves once loaded (and synced if online). */
export async function openHeadless(docId) {
  const s = new DocSync({ docId, store: store.value, connect: connector(docId), dev: localStorage.getItem('dp:dev') || 'dev', toJSON, applyJSON });
  await s.start();
  if (online.value) {
    await new Promise(resolve => {
      const t = setTimeout(resolve, 6000);
      s.on('synced', () => { clearTimeout(t); resolve(); });
      s.on('fatal', () => { clearTimeout(t); resolve(); });
    });
  }
  return s;
}

/** Wait until a sync has nothing unsent (or give up quietly after a while; it's saved locally anyway). */
export const settle = (s, ms = 6000) => new Promise(resolve => {
  const end = Date.now() + ms;
  const tick = () => (s.unsynced() === 0 && s.inflight.size === 0) || Date.now() > end || !online.value ? resolve() : setTimeout(tick, 100);
  setTimeout(tick, 150);
});

function MergeBox({ copyId, copySync, origSync, done }) {
  const [state, setState] = useState({ loading: true });
  const [choices, setChoices] = useState({});
  const [busy, setBusy] = useState(false);
  const copyMeta = findDoc(copyId);

  useEffect(() => {
    let orig = null, copy = null, alive = true;
    (async () => {
      try {
        const parentId = copyMeta?.parentId;
        if (!parentId) throw new Error('I can\'t tell which doc this copy came from.');
        orig = origSync || await openHeadless(parentId);
        copy = copySync || await openHeadless(copyId);
        const baseRaw = copy.doc.getMap('meta').get('base');
        const base = baseRaw ? normalize(JSON.parse(baseRaw)) : toJSON(orig.doc);
        const ours = toJSON(orig.doc), theirs = toJSON(copy.doc);
        const r = merge3(base, ours, theirs);
        if (alive) setState({ r, orig, copy, origTitle: findDoc(parentId)?.title || 'the original' });
      } catch (err) {
        if (alive) setState({ err });
      }
    })();
    return () => {
      alive = false;
      if (orig && orig !== origSync) orig.destroy();
      if (copy && copy !== copySync) copy.destroy();
    };
  }, []);

  const merge = async () => {
    const { r, orig, copy } = state;
    setBusy(true);
    try {
      const result = r.build(choices);
      if (online.value && orig.synced) await saveVersion(orig.id, orig.doc, { kind: 'merge', name: `Before merging "${copyMeta?.title || 'edit copy'}"` }).catch(() => {});
      applyJSON(orig.doc, result, 'dp:merge');
      // the copy now matches, so a later merge starts from here
      applyJSON(copy.doc, result, 'dp:merge');
      copy.doc.getMap('meta').set('base', JSON.stringify(result));
      await orig.flushLocal(); await copy.flushLocal();
      await settle(orig);
      if (online.value && orig.synced) await saveVersion(orig.id, orig.doc, { kind: 'merge', name: `Merged changes from "${copyMeta?.title || 'edit copy'}"` }).catch(() => {});
      api(`/api/docs/${copyId}`, { method: 'PATCH', body: { merged: true } }).catch(() => {});
      toast(`Merged into "${state.origTitle}".`, { action: { label: 'Open it', run: () => go(`/d/${orig.id}`) } });
      done(true);
    } catch (err) {
      toast(err, { kind: 'error' });
    } finally { setBusy(false); }
  };

  let body;
  if (state.loading) body = <div class="center-box"><Spinner label="Comparing" /><p class="hint">Lining up the two versions…</p></div>;
  else if (state.err) body = <ProtoCard small error={state.err} />;
  else {
    const { r } = state;
    const changes = r.stats.theirs + r.stats.both;
    body = (
      <>
        <p class="lead">
          {changes || r.conflicts.length
            ? `Your copy's changes will go into "${state.origTitle}".`
            : 'Nothing to merge: the copy has no changes the original doesn\'t already have.'}
          {r.stats.ours > 0 && ` The original also changed since the copy was made (${r.stats.ours} spot${r.stats.ours === 1 ? '' : 's'}); those changes stay.`}
        </p>
        {r.conflicts.length > 0 && (
          <>
            <h3>{r.conflicts.length} {r.conflicts.length === 1 ? w('conflict') : w('conflicts')}: pick what to keep</h3>
            <ConflictList conflicts={r.conflicts} choices={choices} setChoices={setChoices}
              labels={{ ours: `Keep the ${w('original').toLowerCase()}'s`, theirs: 'Keep the copy\'s', oursHead: w('original'), theirsHead: 'Your copy' }} />
          </>
        )}
      </>
    );
  }
  return (
    <Dialog title={w('mergeBack')} wide onClose={() => done(false)}
      actions={<><Button onClick={() => done(false)}>Cancel</Button>
        <Button kind="primary" icon="merge" disabled={!state.r || busy} onClick={merge}>{busy ? 'Merging…' : w('mergeBack')}</Button></>}>
      {body}
    </Dialog>
  );
}

/** The side-by-side chooser, shared with offline conflicts. */
export function ConflictList({ conflicts, choices, setChoices, labels }) {
  return (
    <ol class="conflicts">
      {conflicts.map(c => {
        const pick = choices[c.id] || 'both';
        const set = v => setChoices({ ...choices, [c.id]: v });
        return (
          <li class="conflict" key={c.id}>
            <div class="conflict-sides">
              <div class={`conflict-side ${pick === 'ours' || pick === 'both' ? 'kept' : 'dropped'}`}>
                <h4>{labels.oursHead}</h4>
                {c.ours.length ? <Html node={{ type: 'doc', content: c.ours }} /> : <p class="hint">(deleted here)</p>}
              </div>
              <div class={`conflict-side ${pick === 'theirs' || pick === 'both' ? 'kept' : 'dropped'}`}>
                <h4>{labels.theirsHead}</h4>
                {c.theirs.length ? <Html node={{ type: 'doc', content: c.theirs }} /> : <p class="hint">(deleted here)</p>}
              </div>
            </div>
            <div class="segmented" role="radiogroup" aria-label="What to keep">
              {[['ours', labels.ours], ['theirs', labels.theirs], ['both', 'Keep both']].map(([v, label]) => (
                <button type="button" role="radio" aria-checked={String(pick === v)} class={pick === v ? 'on' : ''} onClick={() => set(v)}>{label}</button>
              ))}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export const mergeDialog = opts => openModal(done => <MergeBox {...opts} done={done} />);
